import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../services/api.ts';
import { useAuth } from '../contexts/AuthContext.tsx';
import { Event, TicketTier } from '../types/index.ts';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Calendar, MapPin, Users, Ticket, ArrowLeft, Share2, Heart, Video, Info, LogIn } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

interface PriceBreakdown {
  ticket_price: number;
  quantity: number;
  subtotal: number;
  service_fee: number;
  service_fee_percentage: number;
  total_amount: number;
  currency: string;
  currency_symbol: string;
}

export default function EventDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [event, setEvent] = useState<Event | null>(null);
  const [ticketTiers, setTicketTiers] = useState<TicketTier[]>([]);
  const [selectedTier, setSelectedTier] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [isPurchasing, setIsPurchasing] = useState(false);
  const [hasTicket, setHasTicket] = useState(false);
  const [priceBreakdown, setPriceBreakdown] = useState<PriceBreakdown | null>(null);

  useEffect(() => {
    if (id) {
      loadEventData();
      // Only check user tickets if logged in
      if (user) {
        checkUserTicket();
      }
    }
  }, [id, user]);

  const loadEventData = async () => {
    try {
      setIsLoading(true);
      const [eventData, tiersData] = await Promise.all([
        api.getEvent(id!),
        api.getTicketTypes(id!)
      ]);
      setEvent(eventData);
      setTicketTiers(tiersData);
      if (tiersData.length > 0) {
        setSelectedTier(tiersData[0].type_id);
      }
    } catch (error: any) {
      toast.error('Failed to load event details');
    } finally {
      setIsLoading(false);
    }
  };

  const checkUserTicket = async () => {
    try {
      const tickets = await api.getMyTickets();
      const eventTicket = tickets.find((t: any) => t.event_id === id && ['valid', 'used'].includes(t.status));
      setHasTicket(!!eventTicket);
    } catch (error) {
      // Ignore error - user may not have tickets
    }
  };

  // Calculate price breakdown when tier is selected
  useEffect(() => {
    const calculatePrice = async () => {
      if (id && selectedTier) {
        try {
          const breakdown = await api.calculatePrice(id, selectedTier, 1);
          setPriceBreakdown(breakdown);
        } catch (error) {
          // Fallback calculation if API fails
          const tier = ticketTiers.find(t => t.type_id === selectedTier);
          if (tier) {
            const subtotal = tier.price;
            const serviceFee = Math.round(subtotal * 0.075 * 100) / 100;
            setPriceBreakdown({
              ticket_price: tier.price,
              quantity: 1,
              subtotal: subtotal,
              service_fee: serviceFee,
              service_fee_percentage: 7.5,
              total_amount: subtotal + serviceFee,
              currency: event?.currency || 'USD',
              currency_symbol: getCurrencySymbol(event?.currency || 'USD')
            });
          }
        }
      }
    };
    calculatePrice();
  }, [id, selectedTier, ticketTiers, event?.currency]);

  const getCurrencySymbol = (currency: string): string => {
    const symbols: Record<string, string> = {
      'USD': '$',
      'CAD': 'C$',
      'EUR': '€',
      'GBP': '£'
    };
    return symbols[currency] || '$';
  };

  const handlePurchase = async () => {
    if (!selectedTier) {
      toast.error('Please select a ticket tier');
      return;
    }

    try {
      setIsPurchasing(true);
      // Create Stripe checkout session
      const session = await api.createCheckoutSession(id!, selectedTier, 1);
      
      // Redirect to Stripe checkout
      if (session.url) {
        window.location.href = session.url;
      } else {
        toast.error('Failed to create checkout session');
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to process purchase');
    } finally {
      setIsPurchasing(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="text-center py-20">
        <p className="text-gray-400 text-lg">Event not found</p>
        <Button onClick={() => navigate('/discover')} className="mt-4">
          Back to Discover
        </Button>
      </div>
    );
  }

  const selectedTierData = ticketTiers.find(t => t.type_id === selectedTier);

  return (
    <div className="space-y-8 pb-20 md:pb-8">
      {/* Back Button */}
      <Button
        variant="ghost"
        onClick={() => navigate('/discover')}
      >
        <ArrowLeft className="h-4 w-4 mr-2" />
        Back to Events
      </Button>

      {/* Hero Image */}
      <div className="relative h-64 md:h-96 rounded-3xl overflow-hidden">
        {event.poster_url || event.image ? (
          <img
            src={event.poster_url || event.image}
            alt={event.title}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full bg-background-subtle flex items-center justify-center">
            <Calendar className="h-32 w-32 text-gray-600" />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/50 to-transparent" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Title & Category */}
          <div className="space-y-3">
            <div className="flex items-center space-x-2">
              {event.category && <Badge variant="secondary">{event.category}</Badge>}
              {event.is_virtual && <Badge variant="outline">Virtual</Badge>}
            </div>
            <h1 className="text-4xl md:text-5xl font-heading font-bold tracking-tight">
              {event.title}
            </h1>
            <p className="text-xl text-gray-400">{event.artist}</p>
          </div>

          {/* Event Info */}
          <Card className="glass border-white/10">
            <CardContent className="p-6 space-y-4">
              <div className="flex items-start space-x-3">
                <Calendar className="h-5 w-5 text-primary mt-1" />
                <div>
                  <p className="font-medium">Date & Time</p>
                  <p className="text-gray-400">
                    {format(new Date(event.date), 'EEEE, MMMM dd, yyyy')}
                  </p>
                  <p className="text-gray-400">
                    {format(new Date(event.date), 'h:mm a')}
                    {event.end_date && ` - ${format(new Date(event.end_date), 'h:mm a')}`}
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <MapPin className="h-5 w-5 text-primary mt-1" />
                <div>
                  <p className="font-medium">Venue</p>
                  <p className="text-gray-400">{event.venue}</p>
                </div>
              </div>

              {/* Capacity - only visible to hosts and admins */}
              {user && (user.role === 'admin' || user.role === 'host') && (
                <div className="flex items-start space-x-3">
                  <Users className="h-5 w-5 text-primary mt-1" />
                  <div>
                    <p className="font-medium">Capacity</p>
                    <p className="text-gray-400">
                      {event.capacity - event.tickets_sold} tickets remaining
                    </p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Description */}
          <Card className="glass border-white/10">
            <CardHeader>
              <CardTitle>About This Event</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-300 whitespace-pre-wrap">{event.description}</p>
            </CardContent>
          </Card>

          {/* Tags */}
          {event.tags && event.tags.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {event.tags.map((tag) => (
                <Badge key={tag} variant="outline">
                  {tag}
                </Badge>
              ))}
            </div>
          )}
        </div>

        {/* Sidebar - Ticket Purchase */}
        <div className="space-y-4">
          <Card className="glass border-white/10 sticky top-20">
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Ticket className="h-5 w-5 text-primary" />
                <span>Select Tickets</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {ticketTiers.length > 0 ? (
                <>
                  {/* Ticket Tiers */}
                  <div className="space-y-3">
                    {ticketTiers.map((tier) => (
                      <div
                        key={tier.type_id}
                        className={`p-4 rounded-lg border cursor-pointer transition-all ${
                          selectedTier === tier.type_id
                            ? 'border-primary bg-primary/10'
                            : 'border-white/10 hover:border-white/20'
                        }`}
                        onClick={() => setSelectedTier(tier.type_id)}
                        data-testid={`ticket-tier-${tier.type_id}`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <h3 className="font-heading font-bold">{tier.name}</h3>
                          <span className="text-lg font-bold text-primary">
                            {getCurrencySymbol(event.currency || 'USD')}{tier.price.toFixed(2)}
                          </span>
                        </div>
                        <p className="text-sm text-gray-400 mb-2">{tier.description}</p>
                        {tier.perks && tier.perks.length > 0 && (
                          <ul className="text-xs text-gray-500 space-y-1">
                            {tier.perks.map((perk, i) => (
                              <li key={i}>• {perk}</li>
                            ))}
                          </ul>
                        )}
                        {/* Availability - only visible to hosts and admins */}
                        {user && (user.role === 'admin' || user.role === 'host') && (
                          <p className="text-xs text-gray-500 mt-2">
                            {tier.available} / {tier.capacity} available
                          </p>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Price Breakdown */}
                  {priceBreakdown && selectedTierData && (
                    <div className="border border-white/10 rounded-lg p-4 space-y-2 bg-white/5" data-testid="price-breakdown">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-400">Ticket Price</span>
                        <span>{priceBreakdown.currency_symbol}{priceBreakdown.subtotal.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-400 flex items-center gap-1">
                          Service Fee ({priceBreakdown.service_fee_percentage}%)
                          <Info className="h-3 w-3 text-gray-500" />
                        </span>
                        <span>{priceBreakdown.currency_symbol}{priceBreakdown.service_fee.toFixed(2)}</span>
                      </div>
                      <div className="border-t border-white/10 pt-2 mt-2">
                        <div className="flex justify-between font-bold">
                          <span>Total</span>
                          <span className="text-primary" data-testid="total-price">
                            {priceBreakdown.currency_symbol}{priceBreakdown.total_amount.toFixed(2)}
                          </span>
                        </div>
                      </div>
                      {/* Tax Notice */}
                      <p className="text-xs text-gray-500 text-center pt-2 italic" data-testid="tax-notice">
                        Sales tax will be calculated at checkout
                      </p>
                    </div>
                  )}

                  {/* Purchase Button */}
                  {user ? (
                    <Button
                      onClick={handlePurchase}
                      disabled={isPurchasing || !selectedTierData || selectedTierData.available === 0}
                      className="w-full bg-primary hover:bg-primary/90 shadow-glow"
                      size="lg"
                      data-testid="purchase-ticket-btn"
                    >
                      {isPurchasing ? (
                        <span>Processing...</span>
                      ) : selectedTierData?.available === 0 ? (
                        <span>Sold Out</span>
                      ) : priceBreakdown ? (
                        <span>Pay {priceBreakdown.currency_symbol}{priceBreakdown.total_amount.toFixed(2)}</span>
                      ) : (
                        <span>Purchase Ticket</span>
                      )}
                    </Button>
                  ) : (
                    <Button
                      onClick={() => navigate('/login', { state: { from: `/event/${id}` } })}
                      className="w-full bg-primary hover:bg-primary/90 shadow-glow"
                      size="lg"
                      data-testid="sign-in-to-purchase-btn"
                    >
                      <LogIn className="h-4 w-4 mr-2" />
                      Sign in to Purchase
                    </Button>
                  )}
                </>
              ) : (
                <p className="text-center text-gray-400 py-4">
                  No tickets available yet
                </p>
              )}
            </CardContent>
          </Card>

          {/* Share & Save */}
          <div className="flex space-x-2">
            <Button variant="outline" className="flex-1 glass-hover" size="sm">
              <Share2 className="h-4 w-4 mr-2" />
              Share
            </Button>
            <Button variant="outline" className="flex-1 glass-hover" size="sm">
              <Heart className="h-4 w-4 mr-2" />
              Save
            </Button>
          </div>

          {/* Virtual Room Access (for ticket holders) */}
          {hasTicket && (
            <Card className="glass border-primary/30">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-bold text-sm">🎉 You have a ticket!</h3>
                    <p className="text-xs text-gray-400">Join virtual meetups with artists</p>
                  </div>
                  <Button 
                    size="sm" 
                    onClick={() => navigate(`/virtual-room/${id}`)}
                    className="shadow-glow"
                  >
                    <Video className="h-4 w-4 mr-2" />
                    Join Room
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}